import { useEffect, useMemo, useState } from 'react';
import { Mail, MailOpen, Save } from 'lucide-react';
import {
  type CompanyLead,
  type LeadStatus,
  LEAD_STATUSES,
  formatDate,
  intentLabel,
  leadStatusMeta,
  timelineLabel,
} from '../lib/companyLeads';

type PipelineFilter = LeadStatus | 'all';

export function ContactSubmissionsPage() {
  const [items, setItems] = useState<CompanyLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const [filter, setFilter] = useState<PipelineFilter>('all');

  useEffect(() => {
    fetch('/api/contact/list', { credentials: 'same-origin' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`${r.status}`);
        const data = (await r.json()) as { submissions: CompanyLead[] };
        setItems(data.submissions);
      })
      .catch(() => setError('Could not load pipeline.'))
      .finally(() => setLoading(false));
  }, []);

  async function markRead(id: string) {
    await fetch('/api/contact/read', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id }),
    });
    setItems((prev) =>
      prev.map((lead) => (lead.id === id ? { ...lead, read: 1 } : lead)),
    );
  }

  async function updateLead(id: string, patch: { status?: LeadStatus; internalNotes?: string; followUpAt?: string | null }) {
    const res = await fetch('/api/contact/update', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id, ...patch }),
    });
    if (!res.ok) {
      const data = (await res.json().catch(() => ({}))) as { error?: string };
      throw new Error(data.error ?? `Update failed (${res.status})`);
    }
    const data = (await res.json()) as { submission: CompanyLead };
    setItems((prev) => prev.map((lead) => (lead.id === id ? data.submission : lead)));
  }

  const unread = items.filter((lead) => !lead.read).length;
  const filtered = useMemo(
    () => (filter === 'all' ? items : items.filter((lead) => lead.status === filter)),
    [filter, items],
  );

  const counts = useMemo(() => {
    return Object.fromEntries(
      LEAD_STATUSES.map((status) => [
        status.value,
        items.filter((lead) => lead.status === status.value).length,
      ]),
    ) as Record<LeadStatus, number>;
  }, [items]);

  return (
    <div className="page-wrap">
      <header className="page-header">
        <div>
          <div className="eyebrow">Scorekeeper HQ</div>
          <h1 className="page-title">Pipeline</h1>
          <p className="muted" style={{ marginTop: 6 }}>
            Track public inquiries from first touch through demo, proposal, and won customer.
          </p>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            <button
              type="button"
              className={`chip ${filter === 'all' ? 'chip--accent' : 'chip--info'}`}
              onClick={() => setFilter('all')}
            >
              All {items.length}
            </button>
            {LEAD_STATUSES.map((status) => (
              <button
                key={status.value}
                type="button"
                className={`chip ${filter === status.value ? `chip--${status.tone}` : 'chip--info'}`}
                onClick={() => setFilter(status.value)}
              >
                {status.label} {counts[status.value]}
              </button>
            ))}
          </div>
        </div>
        {unread > 0 && (
          <span className="chip chip--info" style={{ marginTop: 4 }}>
            {unread} unread
          </span>
        )}
      </header>

      {loading && <p className="muted" style={{ padding: 24 }}>Loading...</p>}
      {error && <p style={{ color: 'var(--color-danger)', padding: 24 }}>{error}</p>}

      {!loading && !error && filtered.length === 0 && (
        <div className="empty-state" style={{ padding: 48, textAlign: 'center' }}>
          <Mail size={32} style={{ opacity: 0.3, margin: '0 auto 12px' }} />
          <p className="muted">No leads match this view.</p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="stack" style={{ gap: 10 }}>
          {filtered.map((lead) => {
            const status = leadStatusMeta(lead.status);
            const isOpen = open === lead.id;
            return (
              <div key={lead.id} className="card" style={{ overflow: 'hidden' }}>
                <button
                  type="button"
                  className="card--row"
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    cursor: 'pointer',
                    opacity: lead.read ? 0.82 : 1,
                    padding: '16px 20px',
                    border: 'none',
                    background: 'transparent',
                  }}
                  onClick={() => {
                    setOpen(isOpen ? null : lead.id);
                    if (!lead.read) markRead(lead.id);
                  }}
                >
                  <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                    {lead.read
                      ? <MailOpen size={16} style={{ flexShrink: 0, opacity: 0.45 }} />
                      : <Mail size={16} style={{ flexShrink: 0, color: 'var(--accent)' }} />
                    }
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: lead.read ? 600 : 800 }}>
                        {lead.organization_name || lead.name}
                      </div>
                      <div className="muted" style={{ fontSize: 13 }}>
                        {lead.name} - {lead.email}
                      </div>
                    </div>
                    <span className={`chip chip--${status.tone}`} style={{ flexShrink: 0 }}>{status.label}</span>
                    <div className="muted" style={{ fontSize: 12, flexShrink: 0 }}>
                      {formatDate(lead.created_at)}
                    </div>
                  </div>
                </button>

                {isOpen && (
                  <LeadDetail lead={lead} onSave={(patch) => updateLead(lead.id, patch)} />
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function LeadDetail({
  lead,
  onSave,
}: {
  lead: CompanyLead;
  onSave: (patch: { status: LeadStatus; internalNotes: string; followUpAt: string | null }) => Promise<void>;
}) {
  const [status, setStatus] = useState<LeadStatus>(lead.status);
  const [notes, setNotes] = useState(lead.internal_notes ?? '');
  const [followUpAt, setFollowUpAt] = useState(lead.follow_up_at?.slice(0, 10) ?? '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    setStatus(lead.status);
    setNotes(lead.internal_notes ?? '');
    setFollowUpAt(lead.follow_up_at?.slice(0, 10) ?? '');
  }, [lead]);

  const save = async () => {
    setSaving(true);
    setSaved('');
    setError('');
    try {
      await onSave({
        status,
        internalNotes: notes,
        followUpAt: followUpAt || null,
      });
      setSaved(new Date().toLocaleTimeString());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save lead.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        padding: 20,
        borderTop: '1px solid var(--border)',
        background: 'var(--surface-2, var(--surface))',
      }}
    >
      <div className="grid grid--2" style={{ gap: 16 }}>
        <div className="stack" style={{ gap: 12 }}>
          <div className="grid grid--2" style={{ gap: 12 }}>
            <MiniFact label="Intent" value={intentLabel(lead.intent)} />
            <MiniFact label="Timeline" value={timelineLabel(lead.timeline)} />
            <MiniFact label="Role" value={lead.role || 'Not specified'} />
            <MiniFact label="League size" value={lead.league_size || 'Not specified'} />
            <MiniFact label="Phone" value={lead.phone || 'Not specified'} />
            <MiniFact label="Source" value={lead.source_path || 'Direct'} />
          </div>

          <div>
            <div className="field__label">Message</div>
            <div style={{ whiteSpace: 'pre-wrap', marginTop: 6, lineHeight: 1.6 }}>
              {lead.message}
            </div>
          </div>
        </div>

        <div className="stack" style={{ gap: 14 }}>
          <label className="field">
            <span className="field__label">Pipeline status</span>
            <select className="select" value={status} onChange={(e) => setStatus(e.target.value as LeadStatus)}>
              {LEAD_STATUSES.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>

          <label className="field">
            <span className="field__label">Follow-up date</span>
            <input
              className="input"
              type="date"
              value={followUpAt}
              onChange={(e) => setFollowUpAt(e.target.value)}
            />
          </label>

          <label className="field">
            <span className="field__label">Internal notes</span>
            <textarea
              className="textarea"
              rows={6}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Next step, demo notes, concerns, pricing context, or wishlist items"
            />
          </label>

          {error && <p style={{ color: 'var(--color-danger, #e53e3e)', fontSize: 13 }}>{error}</p>}
          <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <button type="button" className="btn btn--primary btn--sm" onClick={save} disabled={saving}>
              <Save size={13} /> {saving ? 'Saving...' : 'Save lead'}
            </button>
            <a className="btn btn--ghost btn--sm" href={`mailto:${lead.email}`}>
              Email lead
            </a>
            {saved && <span className="muted" style={{ fontSize: 12 }}>Saved {saved}</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

function MiniFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="stat__label">{label}</div>
      <div style={{ fontWeight: 700, marginTop: 3, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}
